import { useEffect, useState } from "react";
import { api, formatZAR } from "@/lib/api";
import { Link } from "react-router-dom";
import { ExternalLink, FileText } from "lucide-react";

const STATUS_STYLE = {
  issued:   "bg-blue-50 text-blue-800",
  accepted: "bg-green-50 text-green-800",
  expired:  "bg-zinc-100 text-zinc-600",
};

export default function AdminProformas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/proformas");
      setItems(data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="p-10 max-w-7xl mx-auto" data-testid="admin-proformas">
      <div className="mb-10">
        <div className="text-overline text-zinc-500">Pro-formas</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Issued pro-formas</h1>
        <p className="mt-2 text-sm text-zinc-600">All formal estimates issued — pending acceptance, accepted, or expired.</p>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr className="text-left">
                <Th>Client</Th><Th>Type</Th><Th>Issued</Th><Th>Managed (range)</Th><Th>Assisted (range)</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No pro-formas issued yet.</td></tr>
              ) : items.map((pf) => (
                <tr key={pf.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`proforma-row-${pf.id}`}>
                  <Td>
                    <div className="font-medium">{pf.name}</div>
                    <div className="text-xs text-zinc-500">{pf.email}</div>
                  </Td>
                  <Td className="capitalize">{pf.matter_type}</Td>
                  <Td className="text-zinc-500 text-xs">{new Date(pf.created_at).toLocaleDateString()}</Td>
                  <Td className="font-mono text-xs">
                    {formatZAR(pf.pricing?.managed?.min)} – {formatZAR(pf.pricing?.managed?.max)}
                  </Td>
                  <Td className="font-mono text-xs">
                    {formatZAR(pf.pricing?.assisted?.min)} – {formatZAR(pf.pricing?.assisted?.max)}
                  </Td>
                  <Td>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_STYLE[pf.status] || "bg-zinc-100"}`}>
                      {pf.status}
                    </span>
                  </Td>
                  <Td>
                    <Link to={`/proforma/${pf.id}`} className="text-xs inline-flex items-center gap-1 underline" data-testid={`proforma-view-${pf.id}`}>
                      <FileText className="h-3 w-3" /> View
                    </Link>
                    {pf.matter_id && (
                      <Link to={`/admin/matters/${pf.matter_id}`} className="ml-3 text-xs inline-flex items-center gap-1 underline">
                        <ExternalLink className="h-3 w-3" /> Matter
                      </Link>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-overline text-zinc-500 font-normal">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
